import { computed } from 'vue';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { leadDetail, type LeadDetailSchema } from '@apparatix/schemas/LeadDetail';
import { opportunityDetail, type OpportunityDetailSchema } from '@apparatix/schemas/OpportunityDetail';
import type { Opportunity, OpportunityType, RevenueData } from '@apparatix/types/Leads';
import { OwnerSchema } from '@apparatix/schemas/Owner';
import { OpportunityStatusSchema } from '@apparatix/schemas/OpportunityStatus';
import { TemperatureSchema } from '@apparatix/schemas/Temperature';
import { OfficeSchema } from '@apparatix/schemas/Office';
import { ConfidenceIntervalSchema } from '@apparatix/schemas/ConfidenceInterval';
import { CampaignIntervalSchema } from '@apparatix/schemas/CampaignInterval';

type InitialValues<T extends OpportunityType> = T extends 'Lead'
  ? {[K in keyof LeadDetailSchema]: LeadDetailSchema[K] }
  : {[K in keyof OpportunityDetailSchema]: OpportunityDetailSchema[K] }

export type Selections = {
  owner: Array<Required<OwnerSchema>>,
  status: Array<Required<OpportunityStatusSchema>>,
  temperature: Array<Required<TemperatureSchema>>,
  office?: Array<Required<OfficeSchema>>,
  confidence?: Array<Required<ConfidenceIntervalSchema>>,
  interval?: CampaignIntervalSchema[],
  allStatuses?: Array<Required<OpportunityStatusSchema>>,
  sources: Array<Required<string>>,
}

export const useOpportunityDetailEditor = <T extends OpportunityType>(type: T, selections: Selections, opportunity?: Opportunity, revenueData?: RevenueData) => {

  const getValidationSchema = () => (type === 'Lead' ? leadDetail : opportunityDetail) as typeof opportunityDetail;

  const stringOrDateToDate = (input: string | Date) => {
    if (!input) return null;
    try {
      return new Date(input);
    } catch {
      return null;
    }
  };

  const getLeadInitialValues = (): InitialValues<'Lead'> => ({
    id: opportunity?.id,
    name: opportunity?.name,
    owner: selections?.owner?.find(o => o.id === opportunity.ownerId),
    status: selections?.allStatuses?.find(status => status.id === opportunity.swimlaneId) ?? selections.status[0],
    temperature: opportunity?.temperature ?? selections.temperature[0],
    type: opportunity?.type ?? type,
    value: opportunity?.value,
    source: opportunity?.source,
  });

  // TODO which of these should be using revenue data?
  const getOpportunityInitialValues = (): InitialValues<'Opportunity'> => ({
    ...getLeadInitialValues(),
    anticipatedStart: stringOrDateToDate(opportunity?.anticipatedStart),
    dueDate: stringOrDateToDate(opportunity?.dueDate),
    campaignInterval: selections?.interval?.find(interval => interval.selectionValue === revenueData?.interval),
    confidenceInterval: selections?.confidence?.find(ci => Number(ci.confidencePercentage) === opportunity.ci),
    budget: opportunity?.budget,
    rate: revenueData?.rate,
    periods: revenueData?.periods,
    office: opportunity?.office,
    opportunityLostReasons: opportunity?.lostReasons,
  });

  const initialValues = computed<InitialValues<'Opportunity'>>(() => type === 'Lead' ? getLeadInitialValues() : getOpportunityInitialValues());

  const {
    defineField, errors, meta, resetForm, values,
  } = useForm({
    validationSchema: toTypedSchema(getValidationSchema()),
    initialValues: initialValues.value,
  });

  const [name] = defineField('name');
  const [owner] = defineField('owner');
  const [status] = defineField('status');
  const [temperature] = defineField('temperature');
  const [value] = defineField('value');
  const [source] = defineField('source');
  const [opportunityType] = defineField('type');
  const [anticipatedStart] = type === 'Opportunity' ? defineField('anticipatedStart') : [undefined];
  const [dueDate] = type === 'Opportunity' ? defineField('dueDate') : [undefined];
  const [office] = type === 'Opportunity' ? defineField('office') : [undefined];
  const [confidence] = type === 'Opportunity' ? defineField('confidenceInterval') : [undefined];
  const [interval] = type === 'Opportunity' ? defineField('campaignInterval') : [undefined];
  const [budget] = type === 'Opportunity' ? defineField('budget') : [undefined];
  const [rate] = type === 'Opportunity' ? defineField('rate') : [undefined];
  const [periods] = type === 'Opportunity' ? defineField('periods') : [undefined];
  const [opportunityLostReasons] = type === 'Opportunity' ? defineField('opportunityLostReasons') : [undefined];

  const viewComponentValues = new Proxy(values, {
    get(target, prop, receiver) {
      if (!meta.value.valid && meta.value.dirty && (prop in opportunity)) {
        return opportunity[prop];
      }

      return Reflect.get(target, prop, receiver);
    },
  });

  return {
    anticipatedStart,
    budget,
    confidence,
    dueDate,
    errors,
    interval,
    meta,
    name,
    office,
    opportunityLostReasons,
    opportunityType,
    owner,
    periods,
    rate,
    resetForm,
    source,
    status,
    temperature,
    value,
    values,
    viewComponentValues,
  };
};
